import { useIonActionSheet } from '@ionic/react';

import { useCompany } from '~/api/hooks/useCompany';
import { Avatar, initialsFrom } from '~/components/ui/Avatar';
import { Icon } from '~/components/ui/Icon';

interface CompanySwitcherProps {
  /** Versão de uma linha para o cabeçalho; a completa mostra também o CNPJ. */
  compact?: boolean;
}

/**
 * Mostra a empresa em operação e, quando o usuário tem mais de uma, abre a lista para
 * trocar. Só existe uma empresa selecionada por vez: emitir pela errada é o erro que
 * este componente evita.
 */
export function CompanySwitcher({ compact = false }: CompanySwitcherProps): JSX.Element | null {
  const { selected, all, hasMultiple, select } = useCompany();
  const [presentActionSheet] = useIonActionSheet();

  if (!selected) return null;

  const openPicker = (): void => {
    presentActionSheet({
      header: 'Operar como',
      buttons: [
        ...all.map((company) => ({
          text:
            company.id === selected.id ? `${company.razao_social} (atual)` : company.razao_social,
          handler: () => select(company.id),
        })),
        { text: 'Fechar', role: 'cancel' },
      ],
    });
  };

  const label = compact ? (
    <span
      style={{
        fontSize: 12,
        fontWeight: 500,
        color: 'var(--rx-text)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {selected.razao_social}
    </span>
  ) : (
    <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
      <span
        style={{
          display: 'block',
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--rx-text)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {selected.razao_social}
      </span>
      <span className="rx-mono" style={{ fontSize: 10, color: 'var(--rx-text-3)' }}>
        {selected.cpf_cnpj_formatted} · operando como emissor
      </span>
    </span>
  );

  return (
    <button
      type="button"
      onClick={hasMultiple ? openPicker : undefined}
      disabled={!hasMultiple}
      aria-label={hasMultiple ? 'Trocar empresa' : 'Empresa em operação'}
      aria-haspopup={hasMultiple ? 'dialog' : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 6 : 10,
        maxWidth: '100%',
        padding: compact ? '4px 10px 4px 6px' : '10px 12px',
        background: 'var(--rx-surface)',
        border: '0.5px solid var(--rx-border)',
        borderRadius: compact ? 999 : 12,
        color: 'inherit',
        cursor: hasMultiple ? 'pointer' : 'default',
        fontFamily: 'inherit',
      }}
    >
      <Avatar initials={initialsFrom(selected.razao_social)} size={compact ? 20 : 28} />
      {label}
      {hasMultiple && <Icon name="chevron" size={compact ? 12 : 14} color="var(--rx-text-3)" />}
    </button>
  );
}
